import { useMemo } from "react";
import { useT } from "../i18n/useT";
import { useStore } from "../store/store";
import { computeKPIs } from "@mensajeando/reports";

export function Dashboard() {
  const t = useT();
  const campaigns = useStore((s) => s.campaigns);
  const events = useStore((s) => s.events);

  const live = campaigns.find((c) => c.status === "live") ?? campaigns[0];

  const kpis = useMemo(() => {
    if (!live) return undefined;
    return computeKPIs(events, {
      campaignId: live.id,
      startedConversations: live.startedConversations,
      completedConversations: live.completedConversations,
    });
  }, [events, live]);

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t.nav.dashboard}</h1>
          <p className="text-sm text-slate-500">{live ? live.name : "—"}</p>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-800">
          <span className="relative grid h-2 w-2 place-items-center">
            <span className="absolute inset-0 animate-ping rounded-full bg-brand-500/70" />
            <span className="relative h-2 w-2 rounded-full bg-brand-500" />
          </span>
          {t.dashboard.live}
        </span>
      </header>

      {!kpis && <EmptyState />}
      {kpis && (
        <>
          <section className="grid grid-cols-4 gap-4 mb-6">
            <Kpi label={t.dashboard.sent} value={kpis.totals.sent} tone="brand" />
            <Kpi label={t.dashboard.delivered} value={kpis.totals.delivered} tone="brand" />
            <Kpi label={t.dashboard.failed} value={kpis.totals.failed} tone="ink" />
            <Kpi label={t.dashboard.optedOut} value={kpis.totals.optOuts} tone="accent" />
            <Kpi label={t.dashboard.responseRate} value={pct(kpis.rates.responseRate)} tone="brand" />
            <Kpi label={t.dashboard.completionRate} value={pct(kpis.rates.completionRate)} tone="brand" />
            <Kpi label="Delivery rate" value={pct(kpis.rates.deliveryRate)} tone="brand" />
            <Kpi label="Opt-out rate" value={pct(kpis.rates.optOutRate)} tone="accent" />
          </section>

          <section className="grid grid-cols-2 gap-4">
            <div className="card card-pad">
              <h2 className="font-semibold mb-3">{t.dashboard.byLanguage}</h2>
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-slate-500">
                  <tr><th>Lang</th><th>Sent</th><th>Delivered</th><th>Inbound</th><th>Opt-outs</th></tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-100"><td>EN</td><td>{kpis.byLanguage.en.sent}</td><td>{kpis.byLanguage.en.delivered}</td><td>{kpis.byLanguage.en.inbound}</td><td>{kpis.byLanguage.en.optOuts}</td></tr>
                  <tr className="border-t border-slate-100"><td>ES</td><td>{kpis.byLanguage.es.sent}</td><td>{kpis.byLanguage.es.delivered}</td><td>{kpis.byLanguage.es.inbound}</td><td>{kpis.byLanguage.es.optOuts}</td></tr>
                </tbody>
              </table>
            </div>
            <div className="card card-pad">
              <h2 className="font-semibold mb-3">Answer distribution</h2>
              {Object.keys(kpis.answerDistribution).length === 0 && (
                <p className="text-sm text-slate-500">No responses yet. Send the campaign from the Manual send page to populate metrics.</p>
              )}
              {Object.entries(kpis.answerDistribution).map(([k, vals]) => (
                <div key={k} className="mb-3">
                  <div className="text-xs uppercase text-slate-500 mb-1">{k}</div>
                  <div className="space-y-1">
                    {Object.entries(vals).map(([v, c]) => (
                      <Bar key={v} label={v} value={c} max={Math.max(...Object.values(vals))} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, tone = "brand" }: { label: string; value: number | string; tone?: "brand" | "accent" | "ink" }) {
  const stripe = tone === "accent" ? "bg-accent-400" : tone === "ink" ? "bg-ink-900" : "bg-brand-400";
  return (
    <div className="kpi">
      <div aria-hidden className={`absolute left-0 top-0 h-1 w-10 ${stripe}`} />
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}

function Bar({ label, value, max }: { label: string; value: number; max: number }) {
  const w = max ? Math.round((value / max) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs">
        <span className="text-slate-700">{label}</span>
        <span className="text-slate-500">{value}</span>
      </div>
      <div className="h-2 rounded bg-slate-100 overflow-hidden">
        <div className="h-full bg-brand-600" style={{ width: `${w}%` }} />
      </div>
    </div>
  );
}

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function EmptyState() {
  return (
    <div className="card card-pad">
      <p className="text-sm text-slate-600">No live campaign yet. Create one under <span className="font-medium">Campaigns</span>.</p>
    </div>
  );
}
